import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService from '../services/auth';

interface User {
  username: string;
  userId: string;
  email?: string;
  givenName?: string;
  familyName?: string;
  'custom:role'?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<{ success: boolean; error?: string; requiresPasswordChange?: boolean; requiresMFA?: boolean; challengeData?: any }>;
  signUp: (params: {
    username: string;
    password: string;
    email: string;
    givenName: string;
    familyName: string;
    gender?: string;
    usta_level?: string;
  }) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  confirmSignUp: (username: string, code: string) => Promise<{ success: boolean; error?: string }>;
  resendCode: (username: string) => Promise<{ success: boolean; error?: string }>;
  completeNewPassword: (newPassword: string) => Promise<{ success: boolean; error?: string }>;
  respondToMFAChallenge: (code: string, session: string, username?: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user is already authenticated on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const result = await authService.getCurrentUser();
      if (result.success && result.user) {
        // Get user attributes including custom:role
        const session = await authService.getCurrentSession();
        const idToken = session.session?.tokens?.idToken;
        const customRole = idToken?.payload?.['custom:role'] as string | undefined;
        
        setUser({
          username: result.user.username,
          userId: result.user.userId,
          email: result.user.signInDetails?.loginId,
          'custom:role': customRole || 'player', // Default to player if not set
        });
      } else {
        // If getCurrentUser fails, ensure user is null
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (username: string, password: string) => {
    try {
      const result = await authService.signIn({ username, password });
      if (result.success) {
        await checkAuth();
        return { success: true };
      }
      // Pass through challenge-related flags (MFA, password change, etc.)
      if (result.requiresMFA) {
        return {
          success: false,
          requiresMFA: true,
          challengeData: result.challengeData,
          error: result.error,
        };
      }
      if (result.requiresPasswordChange) {
        return {
          success: false,
          requiresPasswordChange: true,
          challengeData: result.challengeData,
          error: result.error,
        };
      }
      // If there's an existing session error, provide more context
      if (result.error && result.error.includes('already a signed in user')) {
        return { 
          success: false, 
          error: 'There is already a signed in user. Please sign out first or use the button below.' 
        };
      }
      return { success: false, error: result.error };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const signUp = async (params: {
    username: string;
    password: string;
    email: string;
    givenName: string;
    familyName: string;
    gender?: string;
    usta_level?: string;
  }) => {
    try {
      const result = await authService.signUp(params);
      return result;
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const signOut = async () => {
    try {
      await authService.signOut();
      setUser(null);
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const confirmSignUp = async (username: string, code: string) => {
    try {
      const result = await authService.confirmSignUp({ username, code });
      return result;
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const resendCode = async (username: string) => {
    try {
      const result = await authService.resendConfirmationCode(username);
      return result;
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const completeNewPassword = async (newPassword: string) => {
    try {
      const result = await authService.completeNewPassword(newPassword);
      if (result.success) {
        // After password change, refresh auth state
        await checkAuth();
      }
      return result;
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const respondToMFAChallenge = async (code: string, session: string, username?: string) => {
    try {
      const result = await authService.respondToMFAChallenge(code, session, username);
      if (result.success) {
        // After MFA verification, refresh auth state
        await checkAuth();
      }
      return result;
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    confirmSignUp,
    resendCode,
    completeNewPassword,
    respondToMFAChallenge,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
